import { useLocation } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import InfoHint from './InfoHint'
import { findMenuEntry } from '../app/navigation'
import { kit } from './strings'
import './PageHeader.css'

/**
 * The page's opening line: what this page is, and what you can do here.
 *
 * Not a card (general-style pass, 2026-10): the title sits on the page field and
 * the actions at its far end. Beside the title, a SOFT emblem — the menu section's
 * glyph on a light tint of the identity (Phase 1 update) — so each page opens with
 * one quiet touch of the client's colour and the same mark the rail shows for it.
 * It is a tint, never a filled tile, and it carries no glow or halo.
 *
 * The emblem defaults to the icon of the menu section the route belongs to; pass
 * `icon` to override it, or `icon={null}` for a page with no mark.
 *
 * @param {import('react').ReactNode} [hint]  guidance too long to sit on the page
 *   as a paragraph. It goes behind the "i" beside the title.
 * @param {() => void} [onBack]  makes this a page you came to FROM somewhere: a
 *   details screen, a report about one row. See `useGoBack` for what it should do.
 *   A page reached from the menu has no origin and takes none.
 * @param {import('react').ReactNode} [scope]  the page-level switcher (which list
 *   am I on), set on its own row under the title.
 */
export default function PageHeader({
  title,
  subtitle,
  hint,
  count,
  actions,
  scope,
  icon,
  onBack,
  backLabel = kit.back,
}) {
  const { pathname } = useLocation()
  const Emblem = icon === undefined ? findMenuEntry(pathname)?.group.icon : icon

  return (
    <header className="page-header" data-testid="page-header">
      <div className="page-header__bar">
        <div className="page-header__identity">
          {onBack && (
            <button
              type="button"
              className="page-header__back"
              onClick={onBack}
              aria-label={backLabel}
              title={backLabel}
              data-testid="page-back"
            >
              <ArrowRight className="ltr-flip" size={18} strokeWidth={1.75} />
            </button>
          )}

          {Emblem && (
            <span className="page-header__emblem" aria-hidden="true">
              <Emblem size={20} strokeWidth={1.75} />
            </span>
          )}

          <div className="page-header__titles">
            {/* The count rides WITH the title: a fact about what the title names. It
                stays OUTSIDE the h1 so the heading a screen reader announces is the
                page name and nothing else. */}
            <div className="page-header__headline">
              <h1 className="page-header__title" data-testid="page-title">
                <bdi>{title}</bdi>
                {hint && <InfoHint content={hint} />}
              </h1>

              {count != null && (
                <span className="page-header__count">{count.toLocaleString('en-US')}</span>
              )}
            </div>

            {subtitle && (
              <p className="page-header__subtitle">
                <bdi>{subtitle}</bdi>
              </p>
            )}
          </div>
        </div>

        {actions && <div className="page-header__actions">{actions}</div>}
      </div>

      {scope && <div className="page-header__scope">{scope}</div>}
    </header>
  )
}
